import {
    Box,
    Typography,
    TextField,
    IconButton,
    styled,
    CircularProgress,
    InputAdornment,
    Autocomplete,
    FormControl,
    Select,
    InputLabel,
    MenuItem,
    ListSubheader,
    Tooltip,
    Skeleton,
} from '@mui/material';
import { Remove, OpenInNew } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useRecoilValue } from 'recoil';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useQueryProductVersion } from 'src/hooks/useQueryProductVersion';
import { useQueryProducts } from 'src/components/ProductSearchPage/hooks/useQueryProducts';
import { useAvailableLocales } from 'src/hooks/useAvailableLocales';
import { config } from 'src/lib/config';
import { DEFAULT_CULTURE } from 'src/constants';
import { isValidPrd } from 'src/utils/productKeyValidation';

export type ProductOptionsMap = Record<string, string>;
type Row = { id: string; key: string; value: string };

export type TargetProductOptionsProps = {
    productKey: string;
    version?: number;
    culture: string;
    onChangeProductKey: (prd: string) => void;
    onChangeVersion: (version?: number) => void;
    onChangeOptions?: (opts: ProductOptionsMap) => void;
    onChangeCulture: (culture: string) => void;
    initialOptions?: ProductOptionsMap;
};

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiInputBase-root.Mui-focused': {
        backgroundColor: theme.palette.custom.lightBlue,
    },
}));

const Card = styled(Box)(({ theme }) => ({
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
}));

const RowGrid = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr auto',
    gap: theme.spacing(2),
    alignItems: 'start',
    marginBottom: theme.spacing(2),
}));

const StyledInputAdornment = styled(InputAdornment)(({ theme }) => ({
    height: '100%',
    position: 'absolute',
    right: theme.spacing(6),
    top: `calc(50% - ${theme.spacing(3)})`,
}));

const StyledHeaderGrid = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '4fr 3fr 2fr',
    gap: theme.spacing(2),
}));

const uid = (): string => uuidv4();

const rowsToOptions = (rows: Row[]): ProductOptionsMap => {
    const options: ProductOptionsMap = {};

    rows.forEach((row) => {
        const k = row.key.trim();
        const v = row.value.trim();

        if (k && v) options[k] = v;
    });
    return options;
};

const isComplete = (r: Row): boolean => r.key.trim().length > 0 && r.value.trim().length > 0;
const isEmptyRow = (r: Row): boolean => r.key.trim() === '' && r.value.trim() === '';

const addEmptyRowIfNeeded = (list: Row[], maxRows: number): Row[] => {
    if (!list.length) return [{ id: uid(), key: '', value: '' }];
    const last = list[list.length - 1];
    const hasEmptyLast = isEmptyRow(last);
    const allComplete = list.every(isComplete);
    const hasIncompleteBeforeLast = list.slice(0, -1).some((r) => !isComplete(r));

    if (hasEmptyLast && hasIncompleteBeforeLast) return list.slice(0, -1);
    if (!hasEmptyLast && allComplete && list.length < maxRows) {
        return [...list, { id: uid(), key: '', value: '' }];
    }
    return list;
};

export const TargetProductOptions = ({
    productKey,
    version,
    culture,
    onChangeProductKey,
    onChangeVersion,
    onChangeOptions,
    onChangeCulture,
    initialOptions,
}: TargetProductOptionsProps): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);

    const [rows, setRows] = useState<Row[]>([]);
    const bottomRef = useRef<HTMLDivElement | null>(null);

    const [lastKey, setLastKey] = useState<string>('');
    const [lastVersion, setLastVersion] = useState<number | undefined>(undefined);

    const prdCandidate: string = (productKey ?? '').trim();
    const prdIsValid: boolean = isValidPrd(prdCandidate);

    const [appliedInitial, setAppliedInitial] = useState(false);

    const { groupedLocales } = config as unknown as { groupedLocales: Record<string, string[]> };

    const { data: productResp, isLoading } = useQueryProductVersion(
        accessToken,
        { productKey: prdIsValid ? prdCandidate : '', version },
        { enabled: prdIsValid, suspense: false },
    );

    const { data: productsData, isLoading: isLoadingProductName } = useQueryProducts(
        accessToken,
        { productKey: prdIsValid ? prdCandidate : '' },
        { enabled: prdIsValid, suspense: false },
    );

    const activeProduct = productResp?.result?.find((v) => v.isCurrent);
    const product = version
        ? productResp?.result?.find((v) => v.version === version)
        : activeProduct ?? productResp?.result?.[0];

    const productOptions = product?.options ?? [];
    const activeVersion = product?.version;

    const { availableLocales } = useAvailableLocales(
        prdIsValid ? prdCandidate : null,
        activeVersion ?? null,
    );

    const filteredGroupedLocales = Object.entries(groupedLocales).reduce((acc, [region, locales]) => {
        const filtered = availableLocales.length > 0
            ? locales.filter((locale) => availableLocales.includes(locale))
            : locales;

        if (filtered.length > 0) acc[region] = filtered;
        return acc;
    }, {} as Record<string, string[]>);

    const optionNames: string[] = [];
    const valuesByName: Record<string, string[]> = {};

    useEffect(() => {
        if (productKey.trim() === '') {
            setRows([{ id: uid(), key: '', value: '' }]);
        }
    }, [productKey]);

    useEffect(() => {
        if (availableLocales.length > 0 && !availableLocales.includes(culture)) {
            if (availableLocales.includes(DEFAULT_CULTURE)) {
                onChangeCulture(DEFAULT_CULTURE);
            } else {
                const sortedLocales = [...availableLocales].sort();

                onChangeCulture(sortedLocales[0]);
            }
        }
    }, [availableLocales, culture, onChangeCulture]);

    useEffect(() => {
        if (appliedInitial) return;
        const entries = Object.entries(initialOptions ?? {});

        if (!entries.length) return;
        setRows(entries.map(([key, value]) => ({ id: uid(), key, value })));
        setAppliedInitial(true);
    }, [initialOptions, appliedInitial]);

    for (const opt of productOptions) {
        const option = opt as { name?: string; values?: unknown };
        const name = String(option.name ?? '');

        if (name) {
            optionNames.push(name);
            const rawVals = (option.values ?? []) as any[];
            const vals: string[] = Array.isArray(rawVals)
                ? rawVals.map((v: any) => (typeof v === 'string' ? v : String(v?.name ?? ''))).filter(Boolean)
                : [];

            valuesByName[name] = vals;
        }
    }
    optionNames.sort((a, b) => b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' }));
    for (const k of optionNames) {
        valuesByName[k] = [...(valuesByName[k] ?? [])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }

    // When product+version resolves for the first time, seed a single empty row so the user
    // can pick names. Skip if the user has already typed rows or if initialOptions seeded them.
    if (
        prdIsValid
        && product
        && (lastKey !== prdCandidate || lastVersion !== product.version)
        && rows.every(isEmptyRow)
        && !appliedInitial
    ) {
        setRows([{ id: uid(), key: '', value: '' }]);
        setLastKey(prdCandidate);
        setLastVersion(product.version);

        if (version === undefined && activeVersion != null) onChangeVersion(activeVersion);

        onChangeOptions?.({});
    }

    const applyRowChanges = (next: Row[]): void => {
        const normalized = addEmptyRowIfNeeded(next, optionNames.length || next.length);
        const grew = normalized.length > rows.length;

        setRows(normalized);
        onChangeOptions?.(rowsToOptions(normalized));
        if (grew) {
            requestAnimationFrame(() => {
                bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });
        }
    };

    const updateRowKey = (id: string, key: string): void => {
        const normalized = key.trim().toLowerCase();
        const me = rows.findIndex((r) => r.id === id);

        if (optionNames.length > 0 && rows.some((r, i) => i !== me && r.key.trim().toLowerCase() === normalized)) {
            return;
        }
        const vals = valuesByName[key] ?? [];
        const nextValue = vals.length > 0 ? vals[0] : '';

        applyRowChanges(rows.map((r) => (r.id === id ? { ...r, key, value: nextValue } : r)));
    };

    const updateRowValue = (id: string, value: string): void => {
        applyRowChanges(rows.map((r) => (r.id === id ? { ...r, value } : r)));
    };

    const removeRow = (id: string): void => {
        const next = rows.filter((r) => r.id !== id);

        applyRowChanges(next.length ? next : [{ id: uid(), key: '', value: '' }]);
    };

    const handleProductKeyChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const next = e.target.value;

        onChangeProductKey(next);
        onChangeVersion(undefined);
        setLastVersion(undefined);
        setLastKey('');
        setRows([{ id: uid(), key: '', value: '' }]);
    };

    const handleProductVersionChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const raw = e.target.value.trim();

        if (raw === '') {
            onChangeVersion(undefined);
            setLastVersion(undefined);
            return;
        }
        const parsed = Number(raw);

        if (Number.isFinite(parsed)) {
            onChangeVersion(parsed);
            setLastVersion(undefined);
        }
    };

    return (
        <Card>
            <Typography gutterBottom fontWeight="medium" variant="subtitle1">
                Target Product
            </Typography>
            <StyledHeaderGrid>
                <StyledTextField
                    error={productKey.length > 0 && !prdIsValid}
                    helperText={productKey.length > 0 && !prdIsValid ? 'Invalid Product Key.' : ''}
                    InputProps={{
                        endAdornment: (
                            <StyledInputAdornment position="end">
                                {prdIsValid && isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            </StyledInputAdornment>
                        ),
                    }}
                    label="Product Key"
                    placeholder="PRD-1234567"
                    size="small"
                    value={productKey}
                    onChange={handleProductKeyChange}
                />
                <TextField
                    disabled={!prdIsValid}
                    label="Product Version"
                    size="small"
                    value={version ?? ''}
                    onChange={handleProductVersionChange}
                />
                <FormControl fullWidth size="small">
                    <InputLabel id="culture-select-label">Culture</InputLabel>
                    <Select
                        disabled={!prdIsValid || !activeVersion}
                        id="culture-select"
                        label="Culture"
                        labelId="culture-select-label"
                        value={culture}
                        onChange={(e): void => onChangeCulture(e.target.value as string)}
                    >
                        {Object.entries(filteredGroupedLocales).flatMap(([region, locales]) => ([
                            <ListSubheader key={region}>{region}</ListSubheader>,
                            ...locales.map((loc) => (
                                <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                            )),
                        ]))}
                    </Select>
                </FormControl>
            </StyledHeaderGrid>
            {prdIsValid && (
                <Box
                    sx={{
                        my: 2, display: 'flex', alignItems: 'center', gap: 1,
                    }}
                >
                    {isLoadingProductName && (
                        <Skeleton animation="wave" height={24} width={200} />
                    )}
                    {!isLoadingProductName && productsData?.result?.[0]?.matching_versions?.[0]?.productName && (
                        <>
                            <Typography color="text.secondary" variant="body2">
                                {productsData.result[0].matching_versions[0].productName}
                            </Typography>
                            <Tooltip title="View in PIM">
                                <IconButton
                                    sx={{
                                        padding: 0.5,
                                        color: 'text.secondary',
                                        width: 16,
                                        height: 16,
                                        '& .MuiSvgIcon-fontSizeSmall': {
                                            fontSize: '0.875rem',
                                        },
                                    }}
                                    onClick={(): void => window.open(
                                        `https://pim.products.cimpress.io/merchant/products/${prdCandidate}/versions/${version ?? activeVersion}/overview`,
                                        '_blank',
                                    )?.focus()}
                                >
                                    <OpenInNew sx={{ fontSize: '16px', color: 'text.secondary' }} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Box>
            )}
            <Typography fontWeight="medium" sx={{ mb: 1 }} variant="body2">
                Product Options
            </Typography>
            {rows.map((r, idx) => {
                const otherUsed = new Set(
                    rows
                        .filter((_, i) => i !== idx)
                        .map((x) => x.key.trim().toLowerCase())
                        .filter(Boolean),
                );

                const nameOptions: string[] = optionNames.filter(
                    (n) => !otherUsed.has(n.trim().toLowerCase()),
                );
                const valueOptions: string[] = valuesByName[r.key] ?? [];

                return (
                    <RowGrid key={r.id}>
                        <Autocomplete
                            freeSolo
                            disabled={!prdIsValid || !activeVersion}
                            options={nameOptions}
                            renderInput={(params): JSX.Element => (
                                <TextField {...params} label="Option Name" placeholder="e.g., Size" size="small" />
                            )}
                            value={r.key}
                            onInputChange={(_, val): void => updateRowKey(r.id, val)}
                        />
                        <Autocomplete
                            freeSolo
                            disabled={!prdIsValid || !activeVersion}
                            options={valueOptions}
                            renderInput={(params): JSX.Element => (
                                <TextField {...params} label="Option Value" placeholder='e.g., 3"x3"' size="small" />
                            )}
                            value={r.value}
                            onInputChange={(_, val): void => updateRowValue(r.id, val)}
                        />
                        <IconButton
                            aria-label="remove row"
                            disabled={!prdIsValid || !activeVersion || (rows.length <= 1 && idx === rows.length - 1)}
                            size="small"
                            onClick={(): void => removeRow(r.id)}
                        >
                            <Remove />
                        </IconButton>
                    </RowGrid>
                );
            })}
            <div ref={bottomRef} />
        </Card>
    );
};
